/**
 * Plugins v1 del CourseModuleRegistry (F2-A): `lesson` (envuelve la lección
 * existente de learning-blocks) y `resource` (visor de archivo/enlace/texto).
 *
 * Son FÁBRICAS: la app inyecta sus componentes lazy (Solid) al instanciar; el
 * núcleo del plugin (manifest, schemas, contribuciones) vive aquí y es puro y
 * testeable. Los kinds de F3 (assignment/forum/video/live_session) se añaden
 * registrando plugins nuevos — sin tocar este archivo ni el core.
 */

import type { FeatureManifest } from "../country/packs";
import {
  anyObjectValidator,
  type CourseModulePlugin,
  isPlainObject,
  type ModuleCompletionCtx,
  type ModuleGradeCtx,
  type ModuleValidationResult,
  type SchemaValidator,
} from "./types";

/** Loaders de componentes que inyecta la app (ambos opcionales). */
export interface ModuleComponentLoaders<TComponent> {
  authoringComponent?: () => Promise<TComponent>;
  playerComponent?: () => Promise<TComponent>;
}

// ─── lesson ──────────────────────────────────────────────────────────────────

/** Config del módulo lesson: sin campos propios en v1 (el detalle es `lesson`). */
export type LessonModuleConfig = Record<string, unknown>;

const LESSON_MANIFEST: FeatureManifest = {
  key: "academy.module.lesson",
  name: "Lección interactiva",
  description:
    "Lección de bloques de aprendizaje (quiz, flashcards, código…) con corrección server-authoritative y XP.",
  scope: "course",
  status: "stable",
};

/** Contribución de completitud de una lección: 1 si está completada, 0 si no. */
export function lessonCompletionContribution(ctx: ModuleCompletionCtx): number | null {
  if (!ctx.module.ref_id) {
    return null;
  }
  return ctx.lessonProgress?.status === "completed" ? 1 : 0;
}

/** Contribución de nota de una lección: su score autoritativo (0..1). */
export function lessonGradeContribution(ctx: ModuleGradeCtx): number | null {
  if (!ctx.module.ref_id) {
    return null;
  }
  const score = ctx.lessonProgress?.score;
  return typeof score === "number" ? Math.max(0, Math.min(1, score)) : 0;
}

export function createLessonModulePlugin<TComponent = unknown>(
  components: ModuleComponentLoaders<TComponent> = {},
): CourseModulePlugin<TComponent> {
  return {
    kind: "lesson",
    manifest: LESSON_MANIFEST,
    configSchema: anyObjectValidator(),
    authoringComponent: components.authoringComponent,
    playerComponent: components.playerComponent,
    completionContribution: lessonCompletionContribution,
    gradeContribution: lessonGradeContribution,
  };
}

// ─── resource ────────────────────────────────────────────────────────────────

export type ResourceKind = "file" | "link" | "text";

/** Config del módulo resource (espejo de la fila `resource` para validación). */
export interface ResourceModuleConfig extends Record<string, unknown> {
  kind: ResourceKind;
  title?: string;
  url?: string;
  storage_key?: string;
  body?: string;
}

const RESOURCE_MANIFEST: FeatureManifest = {
  key: "academy.module.resource",
  name: "Recurso",
  description: "Material de apoyo del curso: archivo descargable, enlace externo o texto.",
  scope: "course",
  status: "stable",
};

export const RESOURCE_KINDS: readonly ResourceKind[] = ["file", "link", "text"];

/** Type guard del kind de recurso (sin aserciones inseguras). */
export function isResourceKind(kind: string): kind is ResourceKind {
  return kind === "file" || kind === "link" || kind === "text";
}

/** Valida la config de un recurso: kind conocido + el campo que ese kind exige. */
export function validateResourceConfig(
  value: unknown,
): ModuleValidationResult<ResourceModuleConfig> {
  if (!isPlainObject(value)) {
    return { ok: false, errors: ["resource config must be an object"] };
  }
  const { kind } = value;
  if (typeof kind !== "string" || !isResourceKind(kind)) {
    return { ok: false, errors: [`resource kind must be one of ${RESOURCE_KINDS.join("|")}`] };
  }
  const errors: string[] = [];
  if (kind === "link" && (typeof value.url !== "string" || value.url.trim() === "")) {
    errors.push("link resource requires a non-empty url");
  }
  if (kind === "file" && typeof value.storage_key !== "string" && typeof value.url !== "string") {
    errors.push("file resource requires storage_key or url");
  }
  if (kind === "text" && typeof value.body !== "string") {
    errors.push("text resource requires body");
  }
  if (errors.length > 0) {
    return { ok: false, errors };
  }
  const cfg: ResourceModuleConfig = { ...value, kind };
  return { ok: true, value: cfg };
}

const resourceConfigSchema: SchemaValidator<ResourceModuleConfig> = {
  parse: validateResourceConfig,
};

export function createResourceModulePlugin<TComponent = unknown>(
  components: ModuleComponentLoaders<TComponent> = {},
): CourseModulePlugin<TComponent, ResourceModuleConfig> {
  return {
    kind: "resource",
    manifest: RESOURCE_MANIFEST,
    configSchema: resourceConfigSchema,
    authoringComponent: components.authoringComponent,
    playerComponent: components.playerComponent,
    // Un recurso no aporta a completitud ni nota en v1.
    completionContribution: () => null,
    gradeContribution: () => null,
  };
}

// ─── live_session (F3-A · thin) ──────────────────────────────────────────────
//
// Kind DELGADO: el player solo muestra la info de la `class_session` enlazada
// (fecha/hora/aula + placeholder de "unirse más tarde"). La pantalla de JOIN a
// la clase en vivo la posee F3-G; este plugin se mantiene mínimo y marcado beta.
// El enlace con la sesión es por `course_module.ref_id` → `class_session.id`.

/** Config del módulo live_session (todo opcional; el detalle vive en la sesión). */
export interface LiveSessionModuleConfig extends Record<string, unknown> {
  /** id de la class_session enlazada (redundante con course_module.ref_id). */
  session_id?: string;
  /** Provider de la clase en vivo (jitsi|zoom|meet|custom); informativo. */
  provider?: string;
}

const LIVE_SESSION_MANIFEST: FeatureManifest = {
  key: "academy.module.live_session",
  name: "Clase en vivo",
  description:
    "Sesión en vivo enlazada a una clase del horario (fecha/hora/aula), con acceso a la videollamada del proveedor configurado.",
  scope: "course",
  status: "beta",
  // FLAGS: unirse a una clase en vivo necesita AL MENOS UN proveedor de
  // reuniones habilitado (el join resuelve el proveedor por sesión→cohorte→curso).
  requires: [
    "media.provider.jitsi|media.provider.zoom|media.provider.meet|media.provider.custom_link",
  ],
  gateable: true,
  since: "2026-06",
  docsSlug: "profesor",
  implications: {
    navHidden: ["Paradas «Clase en vivo» en Aprender", "«Clase en vivo» en el menú de módulos"],
    flowsBlocked: ["Unirse a la videollamada", "Configurar el enlace de reunión de una sesión"],
    dataPolicy: "preserved",
  },
};

/** Valida la config de live_session: objeto permisivo con campos opcionales. */
export function validateLiveSessionConfig(
  value: unknown,
): ModuleValidationResult<LiveSessionModuleConfig> {
  if (value === null || value === undefined) {
    return { ok: true, value: {} };
  }
  if (!isPlainObject(value)) {
    return { ok: false, errors: ["live_session config must be an object"] };
  }
  return { ok: true, value: value as LiveSessionModuleConfig };
}

export function createLiveSessionModulePlugin<TComponent = unknown>(
  components: ModuleComponentLoaders<TComponent> = {},
): CourseModulePlugin<TComponent> {
  return {
    kind: "live_session",
    manifest: LIVE_SESSION_MANIFEST,
    configSchema: { parse: validateLiveSessionConfig },
    authoringComponent: components.authoringComponent,
    playerComponent: components.playerComponent,
    // Una clase en vivo no aporta a completitud ni nota por sí misma (v1).
    completionContribution: () => null,
    gradeContribution: () => null,
  };
}

// ─── forum (F3-C) ──────────────────────────────────────────────────────────

/** Config del módulo forum: sin campos propios en v1 (el detalle es `forum`). */
export type ForumModuleConfig = Record<string, unknown>;

const FORUM_MANIFEST: FeatureManifest = {
  key: "academy.module.forum",
  name: "Foro",
  description:
    "Foro de discusión de la cohorte: hilos y respuestas (anidadas 1 nivel) con moderación (fijar/cerrar hilos, ocultar posts).",
  scope: "course",
  status: "stable",
  gateable: true,
  since: "2026-06",
  docsSlug: "profesor",
  implications: {
    navHidden: ["Pestaña Foro de la cohorte", "Paradas «Foro» en Aprender"],
    flowsBlocked: ["Crear hilos nuevos", "Publicar respuestas"],
    dataPolicy: "preserved",
  },
};

export function createForumModulePlugin<TComponent = unknown>(
  components: ModuleComponentLoaders<TComponent> = {},
): CourseModulePlugin<TComponent> {
  return {
    kind: "forum",
    manifest: FORUM_MANIFEST,
    configSchema: anyObjectValidator(),
    authoringComponent: components.authoringComponent,
    playerComponent: components.playerComponent,
    // Un foro no aporta a completitud ni nota del curso.
    completionContribution: () => null,
    gradeContribution: () => null,
  };
}

/** Manifest del module kind `forum` (para el puente `pnpm gen:features`). */
export function forumModuleManifest(): FeatureManifest {
  return FORUM_MANIFEST;
}

/** Registry por defecto (sin componentes — para tests y usos server-side). */
export function defaultModuleManifests(): FeatureManifest[] {
  return [LESSON_MANIFEST, RESOURCE_MANIFEST, LIVE_SESSION_MANIFEST, FORUM_MANIFEST];
}

/** Manifests de los kinds de módulo (catálogo auto-descriptivo → gen:features). */
export function courseModuleManifests(): FeatureManifest[] {
  return defaultModuleManifests();
}
